/**
 *  @swagger
 *  definitions:
 *    RegattaContactParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        Name:
 *          description: Name of the RegattaContact
 *          type: string
 *        Role:
 *          description: Role of the RegattaContact
 *          type: string
 *        Email:
 *          description: Email of the RegattaContact
 *          type: string
 *        Phone:
 *          description: Phone of the RegattaContact
 *          type: string
 *        Image:
 *          description: Image of the RegattaContact
 *          type: string
 *
 *  @swagger
 *  definitions:
 *    RegattaContactCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaContactParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - Name
 *            - Role
 *
 *  @swagger
 *  definitions:
 *    RegattaContactQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaContact
 *              type: string
 *        - $ref: '#/definitions/RegattaContactParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaContactUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaContactQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaContact:
 *      allOf:
 *        - $ref: '#/definitions/RegattaContactCreateParams'
 *        - $ref: '#/definitions/RegattaContactUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaContact:
 *        allOf:
 *          - $ref: '#/definitions/RegattaContact'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaContact
 *                $ref: '#/components/schemas/Regatta'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaId: string;
};

export type $Params = {
  Email: null | string;
  Image: null | string;
  Name: string;
  Phone: null | string;
  Role: string;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Regatta?: $Model<'Regatta'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Email: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      isEmail: true,
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Image: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Phone: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Role: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaContact'> => {
  const RegattaContact = stack.define(
    'RegattaContact',
    fields(Sequelize),
    {
      tableName: 'RegattaContacts',
    },
  );

  RegattaContact.associate = (models: $ModelClasses) => {
    models.RegattaContact.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
  };

  RegattaContact.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaContact;
});
